import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, firestore } from '../config/firebase';

const AuthContext = createContext({
  user: null,
  setUser: () => {},
  storeInputs: null,
  setStoreInputs: () => {},
  loading: true,
  signIn: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  resetPassword: async () => {},
  userValidationError: null,
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [storeInputs, setStoreInputs] = useState(null);
  const [userValidationError, setUserValidationError] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Check if user exists in dealer-portal-users collection and has store_id
  const validateUserInFirestore = async (email) => {
    try {
      // Get user document by email (email is used as document ID)
      const userRef = doc(firestore, 'dealer-portal-users', email);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        console.log('User not found in dealer-portal-users collection');
        return { isValid: false };
      }

      const userData = userDoc.data();
      const storeId = userData.store_id;

      if (!storeId) {
        console.log('User found but no store_id');
        return { isValid: false };
      }

      console.log('User validated successfully with store_id:', storeId);
      return { isValid: true, storeId };
    } catch (error) {
      console.error('Error validating user in Firestore:', error);
      return { isValid: false };
    }
  };

  useEffect(() => {
    const init = async () => {
      // 1) Restore from localStorage
      const stored = localStorage.getItem('user');
      if (stored) {
        console.log('Restored user from localStorage');
        setUser(JSON.parse(stored));
      } else {
        console.log('No user found in localStorage');
      }

      // 2) Listen for Firebase auth changes
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        await syncUser(firebaseUser);
      });

      return unsubscribe;
    };

    init().then(unsubscribe => {
      return () => {
        if (unsubscribe) unsubscribe();
      };
    });
  }, []);

  // Map Firebase user → your User + localStorage
  const syncUser = async (firebaseUser) => {
    console.log('syncUser called with firebaseUser:', firebaseUser ? firebaseUser.email : 'null', 'isInitialLoad:', isInitialLoad);
    
    if (firebaseUser) {
      setIsInitialLoad(false);
      try {
        // First validate user in Firestore
        const validation = await validateUserInFirestore(firebaseUser.email || '');
        
        if (!validation.isValid) {
          console.log('User validation failed');
          setUserValidationError("Contact your supplier's admin to activate your account");
          setUser(null);
          localStorage.removeItem('user');
          setLoading(false);
          return;
        }

        // Clear any previous validation errors
        setUserValidationError(null);
        console.log('User validation passed');

        // User is validated, proceed with getting user data
        try {
          // get user from dealer-portal-users collection
          const userRef = doc(firestore, 'dealer-portal-users', firebaseUser.email || '');
          const userDoc = await getDoc(userRef);
          
          if (!userDoc.exists()) {
            setUserValidationError("User not found");
            setUser(null);
            localStorage.removeItem('user');
            setLoading(false);
            return;
          }

          const userData = userDoc.data();
          const usr = {
            id: userData.id,
            email: userData.email || '',
            store_id: userData.store_id || 0,
            store_name: userData.store_name || 'Unknown Store: Please Contact Admin',
            store_email: userData.store_email || '',
            default_profit: userData.default_profit || 0,
            // Per-dealer product visibility (hide-list; empty = see everything)
            hidden_items: userData.hidden_items || {
              subcategories: [],
              products: [],
              variationItems: [],
              detailItems: [],
              detailDetailItems: [],
            },
          };
      
          console.log('User created successfully:', usr.email);
          console.log('Complete user data:', usr);
          setUser(usr);
          localStorage.setItem('user', JSON.stringify(usr));
        } catch (error) {
          console.error('Error getting user details from Firestore:', error);
          setUserValidationError("Error loading user data. Please try again.");
          setUser(null);
          localStorage.removeItem('user');
        }
      } catch (error) {
        console.error('Error in syncUser:', error);
        setUserValidationError("An error occurred during login. Please try again.");
        setUser(null);
        localStorage.removeItem('user');
      }
    } else {
      // If this is the initial load and Firebase user is null, don't clear the user state
      if (isInitialLoad) {
        console.log('Initial load with null Firebase user, keeping stored user state');
        setIsInitialLoad(false);
      } else {
        console.log('Firebase user is null after initial load, clearing user state');
        setUser(null);
        setUserValidationError(null);
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  };

  const signIn = async (email, password) => {
    setLoading(true);
    setUserValidationError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // User validation will be handled by onAuthStateChanged listener
    } catch (error) {
      console.error('Sign in error:', error);
      setLoading(false);
      throw error;
    }
  };

  const signUp = async (email, password) => {
    setLoading(true);
    setUserValidationError(null);
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      // User validation will be handled by onAuthStateChanged listener
    } catch (error) {
      console.error('Sign up error:', error);
      setLoading(false);
      throw error;
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      await firebaseSignOut(auth);
      setUser(null);
      setUserValidationError(null);
      setIsInitialLoad(false);
      localStorage.removeItem('user');
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  };

  const value = {
    user,
    setUser,
    storeInputs,
    setStoreInputs,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    userValidationError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 